'use client';

/**
 * CallBotCapabilities — what CallBot already knows + what you tell it.
 *
 * Two-column "auto-context vs. custom instructions" lifted from the
 * docs (29.3). Differentiates SUM's CallBot from a generic chatbot:
 * it inherits your business profile (name, hours, language, links,
 * loyalty program), so setup is "add what we don't already know"
 * not "feed us your business from scratch".
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiCheckCircle, HiPencilSquare } from 'react-icons/hi2';

type Props = { lang: Locale };

const CallBotCapabilities = ({ lang }: Props) => {
  const { t } = useTranslation(lang);
  const auto = t('CallBot/Capabilities/auto', { returnObjects: true }) as string[];
  const youAdd = t('CallBot/Capabilities/you_add', { returnObjects: true }) as string[];

  return (
    <section
      id="capabilities"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="03" label={t('CallBot/Capabilities/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('CallBot/Capabilities/title1')}{' '}
          <span style={{ color: '#B4C1D8' }}>{t('CallBot/Capabilities/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('CallBot/Capabilities/subtitle')}
        </p>

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <article
            className="rounded-2xl p-7"
            style={{
              background: 'rgba(167,139,250,0.06)',
              border: '1px solid rgba(167,139,250,0.30)',
            }}
          >
            <div
              className="inline-flex items-center justify-center rounded-lg mb-4"
              style={{
                width: 40,
                height: 40,
                background: 'rgba(167,139,250,0.12)',
                border: '1px solid rgba(167,139,250,0.30)',
              }}
            >
              <HiCheckCircle style={{ width: 20, height: 20, color: '#A78BFA' }} />
            </div>
            <h3
              className="font-semibold"
              style={{
                fontSize: 19,
                color: '#F5F7FA',
                margin: '0 0 14px',
                letterSpacing: '-0.01em',
              }}
            >
              {t('CallBot/Capabilities/auto_title')}
            </h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {auto.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3"
                  style={{
                    padding: '8px 0',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <HiCheckCircle
                    className="flex-shrink-0"
                    style={{ width: 16, height: 16, color: '#A78BFA', marginTop: 3 }}
                  />
                  <span style={{ color: '#B4C1D8', fontSize: 14, lineHeight: 1.5 }}>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <article
            className="rounded-2xl p-7"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div
              className="inline-flex items-center justify-center rounded-lg mb-4"
              style={{
                width: 40,
                height: 40,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <HiPencilSquare style={{ width: 20, height: 20, color: '#F5F7FA' }} />
            </div>
            <h3
              className="font-semibold"
              style={{
                fontSize: 19,
                color: '#F5F7FA',
                margin: '0 0 14px',
                letterSpacing: '-0.01em',
              }}
            >
              {t('CallBot/Capabilities/you_title')}
            </h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {youAdd.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3"
                  style={{
                    padding: '8px 0',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <HiPencilSquare
                    className="flex-shrink-0"
                    style={{ width: 16, height: 16, color: '#B4C1D8', marginTop: 3 }}
                  />
                  <span style={{ color: '#B4C1D8', fontSize: 14, lineHeight: 1.5 }}>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
};

export default CallBotCapabilities;
